import React from 'react';
import { Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { PhotoCaptureField } from './PhotoCaptureField';
import { ConsignesPhotoAvantApres } from './ConsignesPhotoAvantApres';
import { apiFetch } from '../../lib/api';

/**
 * Photos avant/après PAR TYPE D'ARTICLE (2026-10-20, demande d'Emeni et de son collègue).
 *
 * Une carte par type d'article du rendez-vous (« Chaises », « Canapés »…). Chaque carte porte une ou
 * plusieurs PAIRES avant/après ; « Ajouter une paire » en crée une de plus (jamais une photo seule), pour
 * que l'avant et l'après restent liés : chaque paire complète peut devenir une réalisation sur le site.
 *
 * Chaque photo part au serveur dès qu'elle est prise (route /prestation-photos) : rien n'est perdu si le
 * téléphone se met en veille. Au démarrage on ne prend que les « avant » ; à la fin, chaque « après » se
 * prend à côté de son « avant », qui sert de repère (même endroit, même angle, même lumière).
 */

type Cote = 'avant' | 'apres';
interface TypeArticle { cle: string; libelle: string }
interface PhotoServeur { id: string; type_cle: string; numero: number; cote: Cote }
export type AppelApi = (chemin: string, init?: RequestInit) => Promise<any>;

const cleSlot = (type: string, numero: number, cote: Cote) => `${type}|${numero}|${cote}`;

/** Appel côté direction (Planning) : apiFetch lève une erreur, on la renvoie sous la même forme que le portail salarié. */
export const appelDirection: AppelApi = (chemin, init) =>
    apiFetch(chemin, init as any).catch((e: any) => ({ error: e?.message || 'Erreur' }));

/** Message d'erreur si des articles n'ont pas encore leurs photos (null = tout est bon). */
export async function verifierPaires(appel: AppelApi, appointmentId: string, etape: 'demarrer' | 'terminer'): Promise<string | null> {
    try {
        const r = await appel(`/prestation-photos/${encodeURIComponent(appointmentId)}/manquants?etape=${etape}`);
        const manquants: string[] = Array.isArray(r?.manquants) ? r.manquants : [];
        if (!manquants.length) return null;
        return etape === 'demarrer'
            ? `Photo avant manquante pour : ${manquants.join(', ')}.`
            : `Il manque une paire complète (avant + après) pour : ${manquants.join(', ')}.`;
    } catch {
        return null; // le serveur revérifie de toute façon à la validation
    }
}

export function PhotosParPaires({ appointmentId, etape, appel, obligatoire }: {
    appointmentId: string;
    /** demarrer : seulement les « avant » ; terminer : les deux côtés. */
    etape: 'demarrer' | 'terminer';
    appel: AppelApi;
    obligatoire: boolean;
}) {
    const [types, setTypes] = React.useState<TypeArticle[] | null>(null);
    const [photos, setPhotos] = React.useState<PhotoServeur[]>([]);
    const [images, setImages] = React.useState<Record<string, string>>({});
    const [enCours, setEnCours] = React.useState<Record<string, boolean>>({});
    const [paires, setPaires] = React.useState<Record<string, number>>({}); // nombre de paires affichées par type
    const [erreur, setErreur] = React.useState('');
    // Paire dont la suppression attend confirmation (« type|numero ») : les deux photos partent ensemble.
    const [aSupprimer, setASupprimer] = React.useState<string | null>(null);

    const base =`/prestation-photos/${encodeURIComponent(appointmentId)}`;

    const appliquer = React.useCallback((liste: PhotoServeur[]) => {
        setPhotos(liste);
        setPaires((avant) => {
            const suite = { ...avant };
            for (const p of liste) suite[p.type_cle] = Math.max(suite[p.type_cle] || 1, Number(p.numero));
            return suite;
        });
    }, []);

    React.useEffect(() => {
        let vivant = true;
        appel(base).then((r: any) => {
            if (!vivant) return;
            if (!r || r.error) { setErreur(r?.error || 'Les photos ne se chargent pas.'); setTypes([]); return; }
            setTypes(r.types || []);
            appliquer(r.photos || []);
            // Les images arrivent une par une, après la liste.
            for (const p of (r.photos || []) as PhotoServeur[]) {
                appel(`${base}/image/${encodeURIComponent(p.id)}`).then((img: any) => {
                    if (vivant && img?.data_url) setImages((m) => ({ ...m, [cleSlot(p.type_cle, Number(p.numero), p.cote)]: img.data_url }));
                }).catch(() => {});
            }
        }).catch(() => { if (vivant) { setErreur('Les photos ne se chargent pas.'); setTypes([]); } });
        return () => { vivant = false; };
    }, [appel, base, appliquer]);

    const envoyer = async (type: string, numero: number, cote: Cote, image: string | null) => {
        const cle = cleSlot(type, numero, cote);
        setErreur('');
        setEnCours((m) => ({ ...m, [cle]: true }));
        try {
            const r = image
                ? await appel(base, { method: 'PUT', body: JSON.stringify({ type_cle: type, numero, cote, image }) })
                : await appel(`${base}?type_cle=${encodeURIComponent(type)}&numero=${numero}${cote === 'apres' ? '&cote=apres' : ''}`, { method: 'DELETE' });
            if (!r || r.error) { setErreur(r?.error || "La photo n'a pas pu être enregistrée."); return; }
            setImages((m) => {
                const suite = { ...m };
                if (image) suite[cle] = image;
                else {
                    delete suite[cleSlot(type, numero, 'apres')];
                    if (cote === 'avant') delete suite[cleSlot(type, numero, 'avant')];
                }
                return suite;
            });
            appliquer(r.photos || []);
            // Paire entière retirée : on ne garde affichées que les paires qui ont encore des photos.
            if (!image && cote === 'avant') {
                const restants = ((r.photos || []) as PhotoServeur[]).filter((p) => p.type_cle === type).map((p) => Number(p.numero));
                setPaires((m) => ({ ...m, [type]: Math.max(1, ...restants) }));
            }
        } catch (e: any) {
            setErreur(e?.message || "La photo n'a pas pu être enregistrée.");
        } finally {
            setEnCours((m) => ({ ...m, [cle]: false }));
        }
    };

    const aPhoto = (type: string, numero: number, cote: Cote) => photos.some((p) => p.type_cle === type && Number(p.numero) === numero && p.cote === cote);

    if (types === null) return <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-content-muted" /></div>;

    return (
        <div className="space-y-3">
            <p className="text-xs text-content-muted">
                {etape === 'demarrer'
                    ? `Une photo avant par article${obligatoire ? ' (obligatoire)' : ' (facultative)'}. « Ajouter une paire » pour en montrer plus.`
                    : `Pour chaque photo avant, prenez l'après au même endroit${obligatoire ? ' : une paire complète par article est obligatoire' : ''}.`}
            </p>
            <ConsignesPhotoAvantApres />
            {types.map((t) => {
                const nb = Math.max(1, paires[t.cle] || 1);
                const completes = Array.from({ length: nb }, (_, i) => i + 1).filter((n) => aPhoto(t.cle, n, 'avant') && aPhoto(t.cle, n, 'apres')).length;
                const avants = Array.from({ length: nb }, (_, i) => i + 1).filter((n) => aPhoto(t.cle, n, 'avant')).length;
                const pret = etape === 'demarrer' ? avants > 0 : completes > 0;
                return (
                    <section key={t.cle} className={`rounded-2xl border p-3 space-y-3 ${pret || !obligatoire ? 'border-border' : 'border-amber-400/70'}`}>
                        <div className="flex items-center justify-between gap-2">
                            <h4 className="text-sm font-bold text-content truncate">{t.libelle}</h4>
                            <span className={`text-[11px] font-semibold shrink-0 ${pret ? 'text-state-ok' : 'text-content-muted'}`}>
                                {pret && <Check size={12} className="inline -mt-0.5 mr-0.5" />}
                                {etape === 'demarrer' ? `${avants} photo${avants > 1 ? 's' : ''} avant` : `${completes} paire${completes > 1 ? 's' : ''} complète${completes > 1 ? 's' : ''}`}
                            </span>
                        </div>
                        {Array.from({ length: nb }, (_, i) => i + 1).map((n) => {
                            const avant = images[cleSlot(t.cle, n, 'avant')] || null;
                            const apres = images[cleSlot(t.cle, n, 'apres')] || null;
                            const avantPris = aPhoto(t.cle, n, 'avant');
                            const occupe = enCours[cleSlot(t.cle, n, 'avant')] || enCours[cleSlot(t.cle, n, 'apres')];
                            return (
                                <div key={n} className="space-y-1.5">
                                    {nb > 1 && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px] font-semibold text-content-muted">Paire {n}</span>
                                            {n === nb && n > 1 && !avantPris && (
                                                <button type="button" onClick={() => setPaires((m) => ({ ...m, [t.cle]: nb - 1 }))}
                                                    className="text-[11px] text-content-muted hover:text-red-500 inline-flex items-center gap-1"><Trash2 size={11} /> Retirer</button>
                                            )}
                                        </div>
                                    )}
                                    <div className={`grid gap-2 ${etape === 'terminer' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                        {avantPris && !avant ? (
                                            <div className="h-32 rounded-xl border border-border flex items-center justify-center"><Loader2 size={16} className="animate-spin text-content-muted" /></div>
                                        ) : etape === 'terminer' && avantPris ? (
                                            // À la fin, l'avant sert de repère : on ne le remplace plus.
                                            <div className="space-y-1.5">
                                                <span className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Avant (repère)</span>
                                                <img src={avant!} alt={`${t.libelle} avant`} className="w-full h-32 object-cover rounded-xl border border-border" />
                                            </div>
                                        ) : (
                                            <PhotoCaptureField label="Avant" value={avant}
                                                onChange={(img) => envoyer(t.cle, n, 'avant', img)} />
                                        )}
                                        {etape === 'terminer' && (
                                            avantPris ? (
                                                <PhotoCaptureField label="Après" value={apres} onChange={(img) => envoyer(t.cle, n, 'apres', img)} />
                                            ) : (
                                                <div className="space-y-1.5">
                                                    <span className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Après</span>
                                                    <div className="h-32 rounded-xl border-2 border-dashed border-border flex items-center justify-center text-center text-[11px] text-content-muted px-2">Prenez d'abord la photo avant</div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                    {occupe && <p className="text-[11px] text-content-muted flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Envoi de la photo…</p>}
                                    {etape === 'terminer' && avantPris && !occupe && (
                                        aSupprimer === `${t.cle}|${n}` ? (
                                            <div className="flex flex-wrap items-center gap-2 rounded-xl bg-red-500/10 px-3 py-2">
                                                <span className="text-[11.5px] text-content flex-1 min-w-[140px]">Supprimer cette paire (avant et après) ?</span>
                                                <button type="button" onClick={() => setASupprimer(null)}
                                                    className="h-8 px-3 rounded-lg border border-border text-[11.5px] font-bold text-content-muted hover:text-content">Annuler</button>
                                                <button type="button" onClick={() => { setASupprimer(null); envoyer(t.cle, n, 'avant', null); }}
                                                    className="h-8 px-3 rounded-lg bg-red-600 text-white text-[11.5px] font-bold hover:bg-red-700">Supprimer</button>
                                            </div>
                                        ) : (
                                            <button type="button" onClick={() => setASupprimer(`${t.cle}|${n}`)}
                                                className="text-[11.5px] text-content-muted hover:text-red-500 inline-flex items-center gap-1 min-h-[32px]">
                                                <Trash2 size={12} /> Supprimer la paire
                                            </button>
                                        )
                                    )}
                                </div>
                            );
                        })}
                        <button type="button" onClick={() => setPaires((m) => ({ ...m, [t.cle]: nb + 1 }))}
                            disabled={nb >= 20 || !aPhoto(t.cle, nb, 'avant')}
                            title={!aPhoto(t.cle, nb, 'avant') ? "Prenez d'abord la photo avant de la paire en cours" : undefined}
                            className="w-full h-9 inline-flex items-center justify-center gap-1.5 rounded-xl border border-border text-xs font-bold text-content-muted hover:text-content hover:border-brand-500 disabled:opacity-40 disabled:hover:border-border">
                            <Plus size={13} /> Ajouter une paire
                        </button>
                    </section>
                );
            })}
            {erreur && <p className="text-xs text-red-400 font-semibold">{erreur}</p>}
        </div>
    );
}
